// Traffic for the admin portal's Traffic page (util/Traffic.java, sql/traffic.sql).
//
// Imported first in index.js, so it reads where the page was opened from before
// anything else runs: the referrer, the UTM tags and click ids in the address, and the
// page. They go to the server once, with the first /startsession (serverApi.js), and the
// tags are then taken out of the address bar, so a copied or shared link doesn't carry
// someone else's campaign.
//
// track(kind, detail) records an event: counted for everyone with no identifier, and
// kept against the visitor only when they accepted analytics cookies (consent.js; the
// server checks the cookie). Events go to /t in small batches every few seconds and when
// the page is hidden. The kinds the server takes are in TrackEvents.java.
import { APP_PATH } from './Layers/layerBasics';
import { apiFetch } from './serverApi';

const TAGS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref',
    'rdt_cid', 'fbclid', 'gclid', 'msclkid', 'ttclid', 'li_fat_id'];
const MAX_BATCH = 50;
const SEND_MS = 5000;

const hasWindow = typeof window !== 'undefined' && typeof document !== 'undefined';

// The app's route patterns (AppRoutes.js), so an address with a postcode or id in it
// counts as its page and nothing personal is sent
const PATTERNS = [
    [/^\/property\/[^/]+\/[^/]+\/?$/, '/property/:postcode/:uprn'],
    [/^\/property\/[^/]+\/?$/, '/property/:postcode'],
    [/^\/listing\/[0-9]+\/?$/, '/listing/:id'],
];
const ROUTES = new Set(['/', '/map', '/big-picture', '/signup', '/confirm', '/forgot-password', '/reset-password', '/profile',
    '/log-in', '/change-password', '/locations', '/subscribe', '/buy-report', '/buy', '/listing', '/reports', '/shortlist',
    '/property', '/area-report', '/your-subscription', '/payment-done', '/terms', '/privacy', '/cookies', '/faq']);

// A path inside the app (without /uk) as its route pattern; '/other' for anything else
export const routePattern = (pathname) => {
    const base = APP_PATH || '';
    let p = pathname || '/';
    if (base && p.startsWith(base)) p = p.slice(base.length) || '/';
    for (const [re, name] of PATTERNS) {
        if (re.test(p)) return name;
    }
    const plain = p.length > 1 ? p.replace(/\/+$/, '') : p;
    return ROUTES.has(plain) ? plain : '/other';
};

// Where this page load came from, read once
const landing = hasWindow ? {
    path: window.location.pathname,
    query: window.location.search.slice(0, 2000),
    referrer: (document.referrer || '').slice(0, 500),
} : {path: '/', query: '', referrer: ''};

// Take the campaign tags out of the address bar (the page has them in `landing`)
if (hasWindow && landing.query && window.history && window.history.replaceState) {
    try {
        const params = new URLSearchParams(landing.query);
        let changed = false;
        TAGS.forEach((t) => {
            if (params.has(t)) {
                params.delete(t);
                changed = true;
            }
        });
        if (changed) {
            const rest = params.toString();
            window.history.replaceState(window.history.state, '', window.location.pathname + (rest ? '?' + rest : '') + window.location.hash);
        }
    } catch (e) {
        // Leave the address as it is
    }
}

let landingSent = false;

// The body of /startsession: the first one of the page load says where it came from;
// later ones (the session ended after an hour idle) only which page is open now
export const sessionBody = () => {
    if (!hasWindow) return {};
    if (landingSent) return {landing: routePattern(window.location.pathname)};
    landingSent = true;
    const path = routePattern(landing.path);
    return {
        landing: path,
        referrer: landing.referrer,
        query: landing.query,
        // An old share link: '/' with the map's state in the query (AppRoutes.js isMapLink)
        share: path === '/' && new URLSearchParams(landing.query).has('s'),
    };
};

// ---- Events ----

let queue = [];
let vitals = [];
let timer = null;

export const track = (kind, detail) => {
    if (!hasWindow) return;
    queue.push({kind, detail: detail == null ? '' : String(detail).slice(0, 120)});
    if (queue.length >= MAX_BATCH) send();
    else schedule();
};

// From reportWebVitals (web-vitals): LCP, FCP, TTFB in ms, CLS as it is; the server
// drops the others
export const trackVital = ({name, value}) => {
    if (!hasWindow || typeof value !== 'number') return;
    vitals.push({name, value, route: routePattern(window.location.pathname)});
    schedule();
};

const schedule = () => {
    if (!timer) timer = setTimeout(send, SEND_MS);
};

export const send = () => {
    if (timer) {
        clearTimeout(timer);
        timer = null;
    }
    if (!queue.length && !vitals.length) return Promise.resolve();
    const body = JSON.stringify({events: queue.slice(0, MAX_BATCH), vitals: vitals.slice(0, MAX_BATCH)});
    queue = queue.slice(MAX_BATCH);
    vitals = vitals.slice(MAX_BATCH);
    if (queue.length || vitals.length) schedule();
    // keepalive, so a batch sent as the page closes still arrives
    return apiFetch('/t', {body, keepalive: true, headers: {'Content-Type': 'application/json'}}).catch(() => {});
};

if (hasWindow) {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') send();
    });
    window.addEventListener('pagehide', send);
}
